import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import { ALL_MATCHES, GROUP_MATCHES, KNOCKOUT_MATCHES, TEAMS } from '../lib/matches';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import WinnerCelebration from '../components/WinnerCelebration';

// Proxy match picker helpers: surface the next upcoming game first so the admin isn't
// scrolling past dozens of played matches deep into the tournament.
const matchKoMs = (m) => new Date(m.ko).getTime();
const nextUpcomingMatchId = () => {
  const now = Date.now();
  const upcoming = ALL_MATCHES.filter(m => matchKoMs(m) > now).sort((a, b) => matchKoMs(a) - matchKoMs(b));
  if (upcoming[0]) return upcoming[0].id;
  const all = [...ALL_MATCHES].sort((a, b) => matchKoMs(b) - matchKoMs(a));
  return all[0]?.id || 'M1';
};

// ── Spreadsheet helpers ───────────────────────────────────────────────────────

// Parse "H-A" score text (e.g. "2-1", "0 - 0"). Returns null if unparseable or not entered.
function parseScore(val) {
  if (val === '' || val === null || val === undefined) return null;
  const str = String(val).trim();
  const m = str.match(/^(\d+)\s*[-–]\s*(\d+)$/);
  if (m) return { homeScore: parseInt(m[1], 10), awayScore: parseInt(m[2], 10) };
  return null;
}

// Detect and parse his "Prediction Entry" sheet format.
// Returns { matchRows, resultColIdx, playerCols } or null if not recognised.
function parseHisFormat(wb) {
  const sheetName = wb.SheetNames.find(n => /prediction.?entry/i.test(n)) || wb.SheetNames[0];
  const ws = wb.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

  const headerIdx = rows.findIndex(r => String(r[0]).trim() === 'Ref');
  if (headerIdx === -1) return null;

  const headers = rows[headerIdx].map(h => String(h).trim());
  const resultColIdx = headers.findIndex(h => h === 'Result');
  if (resultColIdx === -1) return null;

  const playerCols = [];
  for (let i = resultColIdx + 1; i < headers.length; i++) {
    const h = headers[i];
    if (h) {
      const name = h.replace(/['']s?\s+Prediction$/i, '').trim();
      if (name) playerCols.push({ idx: i, name });
    }
  }

  const matchRows = rows.slice(headerIdx + 1).filter(r => /^M\d+$/.test(String(r[0]).trim()));
  return { matchRows, resultColIdx, playerCols };
}

function readWorkbook(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try { resolve(XLSX.read(e.target.result, { type: 'array' })); }
      catch (err) { reject(err); }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

function writeFile(sheetData, sheetName, filename, fmt) {
  const ws = XLSX.utils.aoa_to_sheet(sheetData);
  if (fmt === 'xlsx') {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, `${filename}.xlsx`);
  } else {
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${filename}.csv`; a.click();
    URL.revokeObjectURL(url);
  }
}

// Convert an array of API-returned objects to a spreadsheet download.
function downloadSpreadsheet(rows, filename, fmt) {
  if (!rows || rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const data = [headers, ...rows.map(r => headers.map(h => r[h] ?? ''))];
  writeFile(data, 'Data', filename, fmt);
}

// Generate a results template in his Prediction Entry layout.
function downloadResultsTemplate(users, fmt) {
  const playerHeaders = users.length
    ? users.map(u => `${u.name}'s Prediction`)
    : ["Player's Prediction"];
  const header = ['Ref', 'Home', 'Away', 'KO (UTC)', 'Result', ...playerHeaders];
  const rows = ALL_MATCHES.map(m => [
    m.id,
    m.home !== 'TBD' ? m.home : '',
    m.away !== 'TBD' ? m.away : '',
    m.ko || '',
    '',
    ...users.map(() => ''),
  ]);
  writeFile([header, ...rows], 'Results', `wc2026-results-template`, fmt);
}

// Generate a predictions template with one column per registered player.
function downloadPredictionsTemplate(users, fmt) {
  const playerHeaders = users.length
    ? users.map(u => `${u.name}'s Prediction`)
    : ["Player's Prediction"];
  const header = ['Ref', 'Home', 'Away', ...playerHeaders];
  const rows = ALL_MATCHES.map(m => [
    m.id,
    m.home !== 'TBD' ? m.home : '',
    m.away !== 'TBD' ? m.away : '',
    ...users.map(() => ''),
  ]);
  writeFile([header, ...rows], 'Predictions', `wc2026-predictions-template`, fmt);
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function Admin() {
  const api = useApi();
  const { user: currentUser } = useAuth();

  const isGlobalAdmin = currentUser?.groups?.includes('admin');
  const isLeagueAdmin = isGlobalAdmin || currentUser?.groups?.includes('league-admin');

  const [results, setResults] = useState([]);
  const [users, setUsers] = useState([]);
  const [locked, setLocked] = useState(false);
  const [matchId, setMatchId] = useState('M1');
  const [homeScore, setHomeScore] = useState('');
  const [awayScore, setAwayScore] = useState('');
  // Knockout actual-outcome backup entry (display only; 90' score above is what's scored).
  const [aetHome, setAetHome] = useState('');
  const [aetAway, setAetAway] = useState('');
  const [penHome, setPenHome] = useState('');
  const [penAway, setPenAway] = useState('');
  const [koWinner, setKoWinner] = useState('');
  const [tournamentWinner, setTournamentWinner] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [editEmailUserId, setEditEmailUserId] = useState(null);
  const [editEmailVal, setEditEmailVal] = useState('');
  const [editEmailSaving, setEditEmailSaving] = useState(false);
  const [uploadType, setUploadType] = useState('results');
  const [exportFmt, setExportFmt] = useState('csv');
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const uploadRef = useRef(null);
  const [friendlies, setFriendlies] = useState([]);
  const [friendlyForm, setFriendlyForm] = useState({ id: 'F1', home: '', away: '', ko: '', label: '' });
  const [friendlySaving, setFriendlySaving] = useState(false);
  const [coverageMatchId, setCoverageMatchId] = useState('M1');
  const [coverageData, setCoverageData] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [showScores, setShowScores] = useState(false);
  const [matchdayOverrides, setMatchdayOverrides] = useState({});
  const [matchdayEditId, setMatchdayEditId] = useState('M1');
  const [matchdayEditValue, setMatchdayEditValue] = useState('');
  const [matchdaySaving, setMatchdaySaving] = useState(false);
  const [knockoutTeams, setKnockoutTeams] = useState({});
  const resultFormRef = useRef(null);
  // Match label using resolved knockout teams (from the TEAMS# overrides, which auto-progression
  // keeps current) — so KO selects show real teams as the bracket fills in, not placeholders.
  const koLabel = (m) => {
    const ov = knockoutTeams[m.id];
    const home = ov?.home || m.home;
    const away = ov?.away || m.away;
    return home !== 'TBD' ? `${home} vs ${away}` : (m.label || m.id);
  };
  const [knockoutRound, setKnockoutRound] = useState('R32');
  const [knockoutForm, setKnockoutForm] = useState({});
  const [knockoutSaving, setKnockoutSaving] = useState('');
  const [celebratePreview, setCelebratePreview] = useState(null); // { kind, name, team }
  const [leagues, setLeagues] = useState([]);
  const [joinRequests, setJoinRequests] = useState([]);
  const [joinRequestsLoaded, setJoinRequestsLoaded] = useState(false);
  const [approvingEmail, setApprovingEmail] = useState('');
  const [approveLeague, setApproveLeague] = useState({});
  const [leagueRequests, setLeagueRequests] = useState([]);
  const [leagueRequestsLoaded, setLeagueRequestsLoaded] = useState(false);
  const [approvingLeagueUserId, setApprovingLeagueUserId] = useState('');
  const [leagueRequestNames, setLeagueRequestNames] = useState({});
  const [selectedLeagueId, setSelectedLeagueId] = useState(null);
  const [leagueDetail, setLeagueDetail] = useState(null);
  const [leagueDetailLoading, setLeagueDetailLoading] = useState(false);
  const [leagueName, setLeagueName] = useState('');
  const [leagueCreating, setLeagueCreating] = useState(false);
  const [leagueInviteEmail, setLeagueInviteEmail] = useState('');
  const [leagueAddUserId, setLeagueAddUserId] = useState('');
  const [leagueSaving, setLeagueSaving] = useState('');
  const [renameLeagueName, setRenameLeagueName] = useState('');
  const [leagueAssignUserId, setLeagueAssignUserId] = useState(null);
  const [leagueAssignLeagueId, setLeagueAssignLeagueId] = useState('');
  const [leagueAssigning, setLeagueAssigning] = useState(false);
  // Placeholder user creation
  const [phName, setPhName] = useState('');
  const [phLeagueId, setPhLeagueId] = useState('');
  const [phCreating, setPhCreating] = useState(false);
  // Placeholder invite
  const [phInviteUserId, setPhInviteUserId] = useState('');
  const [phInviteEmail, setPhInviteEmail] = useState('');
  const [phInviting, setPhInviting] = useState('');
  // Proxy predictions
  const [proxyUserId, setProxyUserId] = useState('');
  const [proxyMatchId, setProxyMatchId] = useState(nextUpcomingMatchId);
  const [proxyHome, setProxyHome] = useState('');
  const [proxyAway, setProxyAway] = useState('');
  const [proxyJoker, setProxyJoker] = useState(false);
  const [proxyPreds, setProxyPreds] = useState([]);
  const [proxyPredsLoading, setProxyPredsLoading] = useState(false);
  const [proxyWinner, setProxyWinner] = useState('');
  const [proxyWinnerSaving, setProxyWinnerSaving] = useState(false);
  const [proxyExistingWinner, setProxyExistingWinner] = useState('');
  const [proxyDefaultHome, setProxyDefaultHome] = useState('');
  const [proxyDefaultAway, setProxyDefaultAway] = useState('');
  const [proxyDefaultSaving, setProxyDefaultSaving] = useState(false);
  const [proxyExistingDefault, setProxyExistingDefault] = useState(null);
  const [proxySaving, setProxySaving] = useState(false);
  const [reminderWindow, setReminderWindow] = useState({ minsBefore: 60, windowMins: 15 });
  const [reminderWindowSaving, setReminderWindowSaving] = useState(false);
  const [rebuildState, setRebuildState] = useState('idle'); // idle | running | success | error
  const [rebuildError, setRebuildError] = useState('');
  const [renameUserId, setRenameUserId] = useState(null);
  const [renameNewName, setRenameNewName] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);

  useEffect(() => {
    if (!currentUser) return;
    const ga = currentUser.groups?.includes('admin');
    const la = ga || currentUser.groups?.includes('league-admin');
    Promise.all([
      api.adminGetResults().catch(() => []),
      api.adminGetUsers().catch(() => []),
      api.adminGetLock().catch(() => ({ locked: false })),
      api.getFriendlies().catch(() => []),
      api.getKnockoutTeams().catch(() => []),
      la ? api.adminGetLeagues().catch(() => []) : Promise.resolve([]),
      ga ? api.adminGetJoinRequests().catch(() => []) : Promise.resolve([]),
      ga ? api.adminGetLeagueRequests().catch(() => []) : Promise.resolve([]),
      ga ? api.getMatchdayOverrides().catch(() => ({ overrides: {} })) : Promise.resolve({ overrides: {} }),
      ga ? api.adminGetReminderWindow().catch(() => ({ minsBefore: 120, windowMins: 30 })) : Promise.resolve({ minsBefore: 120, windowMins: 30 }),
    ]).then(([r, u, l, fris, koTeams, lgList, jrs, lrs, mdData, rw]) => {
      setResults(r); setUsers(u); setLocked(l.locked); setFriendlies(fris);
      const koMap = Object.fromEntries(koTeams.map(t => [t.matchId, t]));
      setKnockoutTeams(koMap);
      setKnockoutForm(Object.fromEntries(koTeams.map(t => [t.matchId, { home: t.home, away: t.away }])));
      setLeagues(lgList);
      setJoinRequests(jrs); setJoinRequestsLoaded(true);
      setLeagueRequests(lrs); setLeagueRequestsLoaded(true);
      setLeagueRequestNames(Object.fromEntries(lrs.map(r => [r.userId, r.name])));
      setMatchdayOverrides(mdData?.overrides ?? {});
      setReminderWindow(rw);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [currentUser]); // eslint-disable-line

  // League-admins have exactly one league — auto-select it so the invite form is immediately visible
  useEffect(() => {
    if (!isGlobalAdmin && leagues.length === 1 && !selectedLeagueId) {
      handleSelectLeague(leagues[0].leagueId);
    }
  }, [leagues]); // eslint-disable-line

  const showMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: '', text: '' }), 5000);
  };

  const handleSetResult = async (e) => {
    e.preventDefault();
    const h = parseInt(homeScore), a = parseInt(awayScore);
    if (isNaN(h) || isNaN(a)) return;
    setSyncing(true);
    const body = { matchId, homeScore: h, awayScore: a };
    const num = (v) => { const n = parseInt(v); return Number.isNaN(n) ? undefined : n; };
    if (aetHome !== '' && aetAway !== '') { body.aetHome = num(aetHome); body.aetAway = num(aetAway); }
    if (penHome !== '' && penAway !== '') { body.penHome = num(penHome); body.penAway = num(penAway); }
    if (koWinner) body.winner = koWinner;
    try {
      await api.adminSetResult(body);
      setResults(prev => [...prev.filter(r => r.matchId !== matchId), { ...body, manualEntry: true }]);
      showMsg('success', `Result saved: ${matchId} = ${h}–${a}`);
      setHomeScore(''); setAwayScore(''); setAetHome(''); setAetAway(''); setPenHome(''); setPenAway(''); setKoWinner('');
    } catch (err) { showMsg('error', err.message); }
    finally { setSyncing(false); }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail) return;
    setSyncing(true);
    try {
      await api.adminInvite({ email: inviteEmail });
      const updated = await api.adminGetUsers();
      setUsers(updated);
      showMsg('success', `Invite sent to ${inviteEmail} — they have 24 hours to sign in.`);
      setInviteEmail('');
    } catch (err) { showMsg('error', err.message); }
    finally { setSyncing(false); }
  };

  const handleSetWinner = async (e) => {
    e.preventDefault();
    if (!tournamentWinner) return;
    setSyncing(true);
    try {
      await api.adminSetWinner({ winner: tournamentWinner });
      showMsg('success', `Tournament winner set: ${tournamentWinner}`);
    } catch (err) { showMsg('error', err.message); }
    finally { setSyncing(false); }
  };

  const handleSetRole = async (userId, role) => {
    try {
      await api.adminSetUserRole(userId, role);
      setUsers(prev => prev.map(u => u.userId === userId ? { ...u, role } : u));
      if (role === 'league-admin' && leagues.length > 0) {
        setLeagueAssignUserId(userId);
        setLeagueAssignLeagueId(leagues[0]?.leagueId || '');
      } else {
        showMsg('success', `Role updated to ${role === 'league-admin' ? 'League Admin' : 'Player'}.`);
      }
    } catch (err) { showMsg('error', err.message); }
  };

  const handleLeagueAssign = async (userId) => {
    if (!leagueAssignLeagueId) { setLeagueAssignUserId(null); showMsg('success', 'Role updated to League Admin.'); return; }
    setLeagueAssigning(true);
    try {
      await api.adminAddToLeague(leagueAssignLeagueId, { userId, isLeagueAdmin: true });
      const lname = leagues.find(l => l.leagueId === leagueAssignLeagueId)?.name || leagueAssignLeagueId;
      showMsg('success', `Role updated to League Admin and added to ${lname}.`);
      if (selectedLeagueId === leagueAssignLeagueId) handleSelectLeague(leagueAssignLeagueId);
    } catch (err) { showMsg('success', 'Role updated to League Admin.'); }
    finally { setLeagueAssignUserId(null); setLeagueAssignLeagueId(''); setLeagueAssigning(false); }
  };

  const handleSetEmail = async (userId) => {
    const trimmed = editEmailVal.trim().toLowerCase();
    if (!trimmed) return;
    setEditEmailSaving(true);
    try {
      await api.adminSetUserEmail(userId, trimmed);
      setUsers(prev => prev.map(u => u.userId === userId ? { ...u, email: trimmed } : u));
      setEditEmailUserId(null);
      showMsg('success', 'Email address updated.');
    } catch (err) {
      showMsg('error', err.message || 'Failed to update email.');
    } finally {
      setEditEmailSaving(false);
    }
  };

  const handleRenamePlayer = async (userId) => {
    const trimmed = renameNewName.trim();
    if (!trimmed) return;
    setRenameSaving(true);
    try {
      await api.adminRenamePlayer(currentUser.sub, userId, trimmed);
      setUsers(prev => prev.map(u => u.userId === userId ? { ...u, name: trimmed } : u));
      setRenameUserId(null);
      showMsg('success', `Player renamed to "${trimmed}".`);
    } catch (err) {
      showMsg('error', err.message || 'Failed to rename player.');
    } finally {
      setRenameSaving(false);
    }
  };

  const handleSetReminderWindow = async (e) => {
    e.preventDefault();
    const minsBefore = parseInt(e.target.minsBefore.value, 10);
    const windowMins = parseInt(e.target.windowMins.value, 10);
    if (isNaN(minsBefore) || isNaN(windowMins)) return;
    setReminderWindowSaving(true);
    try {
      const updated = await api.adminSetReminderWindow(minsBefore, windowMins);
      setReminderWindow(updated);
      showMsg('success', `Reminder window saved: ${updated.minsBefore} mins before, ${updated.windowMins}-min window.`);
    } catch (err) {
      showMsg('error', err.message || 'Failed to save reminder window.');
    } finally {
      setReminderWindowSaving(false);
    }
  };

  const handleRebuildLeaderboard = async () => {
    if (!window.confirm('Rebuild leaderboard snapshots?\n\nThis replays every recorded result in match order and rewrites all position-history snapshots. Safe to run anytime — takes a few seconds.')) return;
    setRebuildState('running');
    setRebuildError('');
    try {
      await api.adminBackfillLeaderboard();
      setRebuildState('success');
      showMsg('success', 'Leaderboard snapshots rebuilt.');
    } catch (err) {
      setRebuildState('error');
      setRebuildError(err.message || 'Rebuild failed');
    }
  };

  const handleDeleteUser = async (userId, name) => {
    if (!window.confirm(`Permanently delete ${name}?\n\nThis will remove their account, all predictions, and cannot be undone.`)) return;
    try {
      await api.adminDeleteUser(userId);
      setUsers(prev => prev.filter(u => u.userId !== userId));
      setLeagueDetail(prev => prev ? { ...prev, members: (prev.members || []).filter(m => m.userId !== userId) } : prev);
      showMsg('success', `${name} has been deleted.`);
    } catch (err) { showMsg('error', err.message); }
  };

  const handleResetPassword = async (userId, name) => {
    if (!window.confirm(`Send a password reset email to ${name}?`)) return;
    try {
      await api.adminResetUserPassword(userId);
      showMsg('success', `Password reset email sent to ${name}.`);
    } catch (err) { showMsg('error', err.message); }
  };

  const handleCreatePlaceholder = async (e) => {
    e.preventDefault();
    if (!phName.trim()) return;
    setPhCreating(true);
    try {
      const res = await api.adminCreatePlaceholder({ name: phName.trim(), leagueId: phLeagueId || undefined });
      setUsers(prev => [...prev, { userId: res.userId, name: res.name, placeholder: true, createdAt: new Date().toISOString() }]);
      showMsg('success', `Placeholder "${res.name}" created (${res.userId})`);
      setPhName(''); setPhLeagueId('');
    } catch (err) { showMsg('error', err.message); }
    finally { setPhCreating(false); }
  };

  const handleInvitePlaceholder = async (e) => {
    e.preventDefault();
    if (!phInviteUserId || !phInviteEmail) return;
    setPhInviting(phInviteUserId);
    try {
      const res = await api.adminInvitePlaceholder(phInviteUserId, { email: phInviteEmail });
      setUsers(prev => prev.map(u => u.userId === phInviteUserId ? { ...u, invitedEmail: phInviteEmail } : u));
      showMsg('success', res.message);
      setPhInviteUserId(''); setPhInviteEmail('');
    } catch (err) { showMsg('error', err.message); }
    finally { setPhInviting(''); }
  };

  const handleDeletePlaceholder = async (userId, name) => {
    if (!window.confirm(`Delete placeholder "${name}"?\n\nAll their predictions will be permanently removed.`)) return;
    try {
      await api.adminDeletePlaceholder(userId);
      setUsers(prev => prev.filter(u => u.userId !== userId));
      setLeagueDetail(prev => prev ? { ...prev, members: (prev.members || []).filter(m => m.userId !== userId) } : prev);
      showMsg('success', `Placeholder "${name}" deleted.`);
    } catch (err) { showMsg('error', err.message); }
  };

  const loadProxyPreds = async (userId) => {
    if (!userId) { setProxyPreds([]); setProxyExistingWinner(''); setProxyExistingDefault(null); return; }
    setProxyPredsLoading(true);
    try {
      const all = await api.adminGetUserPredictions(userId);
      const winnerItem  = all.find(p => p.sk === 'WINNER');
      const defaultItem = all.find(p => p.sk === 'DEFAULT');
      setProxyPreds(all.filter(p => p.matchId));
      setProxyExistingWinner(winnerItem?.winner || '');
      setProxyExistingDefault(defaultItem ? { homeScore: defaultItem.homeScore, awayScore: defaultItem.awayScore } : null);
    } catch { setProxyPreds([]); setProxyExistingWinner(''); setProxyExistingDefault(null); }
    finally { setProxyPredsLoading(false); }
  };

  const handleSaveProxyPrediction = async (e) => {
    e.preventDefault();
    const h = parseInt(proxyHome), a = parseInt(proxyAway);
    if (!proxyUserId || !proxyMatchId || isNaN(h) || isNaN(a)) return;
    setProxySaving(true);
    try {
      if (proxyMatchId === 'DEFAULT') {
        await api.adminSaveProxyDefault({ targetUserId: proxyUserId, homeScore: h, awayScore: a });
        setProxyExistingDefault({ homeScore: h, awayScore: a });
        showMsg('success', `Default score saved: ${h}–${a}`);
      } else {
        await api.adminSaveProxyPrediction({ targetUserId: proxyUserId, matchId: proxyMatchId, homeScore: h, awayScore: a, joker: proxyJoker });
        setProxyPreds(prev => [...prev.filter(p => p.matchId !== proxyMatchId), { matchId: proxyMatchId, homeScore: h, awayScore: a, joker: proxyJoker }]);
        showMsg('success', `Prediction saved: ${proxyMatchId} = ${h}–${a}${proxyJoker ? ' ×2' : ''}`);
      }
      setProxyHome(''); setProxyAway(''); setProxyJoker(false);
    } catch (err) { showMsg('error', err.message); }
    finally { setProxySaving(false); }
  };

  const handleSaveProxyWinner = async (e) => {
    e.preventDefault();
    if (!proxyUserId || !proxyWinner) return;
    setProxyWinnerSaving(true);
    try {
      await api.adminSaveProxyWinner({ targetUserId: proxyUserId, winner: proxyWinner });
      setProxyExistingWinner(proxyWinner);
      showMsg('success', `Winner prediction saved: ${proxyWinner}`);
    } catch (err) { showMsg('error', err.message); }
    finally { setProxyWinnerSaving(false); }
  };

  const handleSaveProxyDefault = async (e) => {
    e.preventDefault();
    const h = parseInt(proxyDefaultHome), a = parseInt(proxyDefaultAway);
    if (!proxyUserId || isNaN(h) || isNaN(a)) return;
    setProxyDefaultSaving(true);
    try {
      await api.adminSaveProxyDefault({ targetUserId: proxyUserId, homeScore: h, awayScore: a });
      setProxyExistingDefault({ homeScore: h, awayScore: a });
      showMsg('success', `Default score saved: ${h}–${a}`);
      setProxyDefaultHome(''); setProxyDefaultAway('');
    } catch (err) { showMsg('error', err.message); }
    finally { setProxyDefaultSaving(false); }
  };

  const handleToggleLock = async () => {
    setSyncing(true);
    try {
      const { locked: newLocked } = await api.adminSetLock(!locked);
      setLocked(newLocked);
      showMsg('success', newLocked
        ? 'Predictions locked — players are now in read-only mode.'
        : 'Predictions unlocked — players can now submit.');
    } catch (err) { showMsg('error', err.message); }
    finally { setSyncing(false); }
  };

  const handleExport = async (type) => {
    setSyncing(true);
    try {
      const rows = type === 'results'
        ? await api.adminExportResults()
        : await api.adminExportPredictions();
      const filename = `wc2026-${type}-${format(new Date(), 'yyyy-MM-dd')}`;
      downloadSpreadsheet(rows, filename, exportFmt);
    } catch (err) { showMsg('error', err.message); }
    finally { setSyncing(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSyncing(true);
    try {
      const wb = await readWorkbook(file);
      const his = parseHisFormat(wb);

      let rows;
      if (his) {
        if (uploadType === 'results') {
          // Extract results from his Result column
          rows = his.matchRows
            .map(r => ({ matchId: String(r[0]).trim(), ...parseScore(r[his.resultColIdx]) }))
            .filter(r => r.homeScore !== undefined);
        } else {
          // Match player column names to registered users, extract predictions
          rows = [];
          const unmatched = [];
          for (const col of his.playerCols) {
            const user = users.find(u =>
              u.name.toLowerCase().startsWith(col.name.toLowerCase()) ||
              col.name.toLowerCase().startsWith(u.name.split(' ')[0].toLowerCase())
            );
            if (!user) { unmatched.push(col.name); continue; }
            for (const r of his.matchRows) {
              const score = parseScore(r[col.idx]);
              if (score) rows.push({ userId: user.userId, matchId: String(r[0]).trim(), ...score, joker: false });
            }
          }
          if (unmatched.length) showMsg('error', `No match for player(s): ${unmatched.join(', ')} — check names match registered players.`);
        }
      } else {
        // Fallback: our flat format (matchId/homeScore/awayScore columns)
        const ws = wb.Sheets[wb.SheetNames[0]];
        rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
      }

      if (!rows.length) { showMsg('error', 'No valid rows found — check the file format.'); return; }

      const result = uploadType === 'results'
        ? await api.adminUploadResults(rows)
        : await api.adminUploadPredictions(rows);

      const noun = uploadType === 'results' ? 'result' : 'prediction';
      const summary = `Uploaded ${result.saved} ${noun}(s)`;
      if (result.errors?.length) {
        showMsg('error', `${summary}, ${result.errors.length} error(s): ${result.errors.slice(0, 3).join('; ')}${result.errors.length > 3 ? '…' : ''}`);
      } else {
        showMsg('success', `${summary}.`);
        if (uploadType === 'results') {
          const fresh = await api.adminGetResults();
          setResults(fresh);
        }
      }
    } catch (err) { showMsg('error', `Upload failed: ${err.message}`); }
    finally {
      setSyncing(false);
      if (uploadRef.current) uploadRef.current.value = '';
    }
  };

  const handleSetMatchdayOverride = async (e) => {
    e.preventDefault();
    const md = parseInt(matchdayEditValue, 10);
    if (!matchdayEditId || isNaN(md) || md < 1 || md > 17) return;
    setMatchdaySaving(true);
    try {
      await api.adminSetMatchdayOverride(matchdayEditId, md);
      setMatchdayOverrides(prev => ({ ...prev, [matchdayEditId]: md }));
      showMsg('success', `${matchdayEditId} assigned to Matchday ${md}`);
    } catch (err) { showMsg('error', `Failed: ${err.message}`); }
    finally { setMatchdaySaving(false); }
  };

  const handleSetKnockoutTeams = async (matchId) => {
    const form = knockoutForm[matchId] || {};
    if (!form.home || !form.away) return;
    setKnockoutSaving(matchId);
    try {
      await api.adminSetKnockoutTeams(matchId, { home: form.home, away: form.away });
      setKnockoutTeams(prev => ({ ...prev, [matchId]: { matchId, home: form.home, away: form.away, manualEntry: true } }));
      showMsg('success', `${matchId} teams set: ${form.home} vs ${form.away}`);
    } catch (err) { showMsg('error', `Failed: ${err.message}`); }
    finally { setKnockoutSaving(''); }
  };

  const handleCreateLeague = async (e) => {
    e.preventDefault();
    if (!leagueName.trim()) return;
    setLeagueCreating(true);
    try {
      const { leagueId, name } = await api.adminCreateLeague({ name: leagueName.trim() });
      setLeagues(prev => [...prev, { leagueId, name }]);
      setLeagueName('');
      showMsg('success', `League "${name}" created.`);
      handleSelectLeague(leagueId);
    } catch (err) { showMsg('error', err.message); }
    finally { setLeagueCreating(false); }
  };

  const handleSelectLeague = async (leagueId) => {
    setSelectedLeagueId(leagueId);
    setLeagueDetail(null);
    setRenameLeagueName('');
    setLeagueDetailLoading(true);
    try {
      const data = await api.adminGetLeague(leagueId);
      setLeagueDetail(data);
    } catch (err) { showMsg('error', `Failed to load league: ${err.message}`); }
    finally { setLeagueDetailLoading(false); }
  };

  const handleLeagueInvite = async (e) => {
    e.preventDefault();
    if (!leagueInviteEmail || !selectedLeagueId) return;
    setLeagueSaving('invite');
    try {
      const res = await api.adminInviteToLeague(selectedLeagueId, { email: leagueInviteEmail });
      showMsg('success', res.message);
      setLeagueInviteEmail('');
      if (!res.alreadyMember) handleSelectLeague(selectedLeagueId); // refresh member list
    } catch (err) { showMsg('error', err.message); }
    finally { setLeagueSaving(''); }
  };

  const handleLeagueAddExisting = async () => {
    if (!leagueAddUserId || !selectedLeagueId) return;
    setLeagueSaving('add');
    try {
      await api.adminAddToLeague(selectedLeagueId, { userId: leagueAddUserId });
      const u = users.find(u => u.userId === leagueAddUserId);
      showMsg('success', `${u?.name || leagueAddUserId} added to league.`);
      setLeagueAddUserId('');
      handleSelectLeague(selectedLeagueId);
    } catch (err) { showMsg('error', err.message); }
    finally { setLeagueSaving(''); }
  };

  const handleRenameLeague = async (e) => {
    e.preventDefault();
    if (!selectedLeagueId || !renameLeagueName.trim()) return;
    setLeagueSaving('rename');
    try {
      const { name } = await api.adminRenameLeague(selectedLeagueId, { name: renameLeagueName.trim() });
      setLeagues(prev => prev.map(l => l.leagueId === selectedLeagueId ? { ...l, name } : l));
      setLeagueDetail(prev => prev ? { ...prev, name } : prev);
      setRenameLeagueName('');
      showMsg('success', `League renamed to "${name}".`);
    } catch (err) { showMsg('error', err.message); }
    finally { setLeagueSaving(''); }
  };

  const handleDeleteLeague = async () => {
    if (!selectedLeagueId) return;
    const league = leagues.find(l => l.leagueId === selectedLeagueId);
    if (!window.confirm(`Permanently delete "${league?.name}"?\n\nThis removes the league and all its memberships. Player accounts and predictions are not affected.`)) return;
    setLeagueSaving('delete');
    try {
      await api.adminDeleteLeague(selectedLeagueId);
      setLeagues(prev => prev.filter(l => l.leagueId !== selectedLeagueId));
      setSelectedLeagueId(null);
      setLeagueDetail(null);
      showMsg('success', `League "${league?.name}" deleted.`);
    } catch (err) { showMsg('error', err.message); }
    finally { setLeagueSaving(''); }
  };

  const handleLeagueAddAll = async () => {
    if (!selectedLeagueId || !leagueDetail) return;
    const memberIds = new Set((leagueDetail.members || []).map(m => m.userId));
    const missing = users.filter(u => !memberIds.has(u.userId));
    if (!missing.length) { showMsg('success', 'All players are already members.'); return; }
    if (!window.confirm(`Add ${missing.length} player(s) to this league?`)) return;
    setLeagueSaving('addall');
    let added = 0, errors = 0;
    for (const u of missing) {
      try {
        await api.adminAddToLeague(selectedLeagueId, { userId: u.userId });
        added++;
      } catch { errors++; }
    }
    showMsg(errors ? 'error' : 'success', `Added ${added} player(s)${errors ? `, ${errors} failed` : ''}.`);
    setLeagueSaving('');
    handleSelectLeague(selectedLeagueId);
  };

  const handleLeagueRemove = async (userId, name) => {
    if (!selectedLeagueId) return;
    if (!window.confirm(`Remove ${name} from this league?`)) return;
    try {
      await api.adminRemoveFromLeague(selectedLeagueId, userId);
      showMsg('success', `${name} removed from league.`);
      handleSelectLeague(selectedLeagueId);
    } catch (err) { showMsg('error', err.message); }
  };

  const handleApproveLeagueRequest = async (userId) => {
    const name = leagueRequestNames[userId];
    if (!name?.trim()) return;
    setApprovingLeagueUserId(userId);
    try {
      await api.adminApproveLeagueRequest({ userId, name: name.trim() });
      setLeagueRequests(prev => prev.filter(r => r.userId !== userId));
      showMsg('success', `League "${name}" created — user promoted to league admin.`);
    } catch (err) { showMsg('error', err.message); }
    finally { setApprovingLeagueUserId(''); }
  };

  const handleDenyLeagueRequest = async (userId) => {
    try {
      await api.adminDenyLeagueRequest({ userId });
      setLeagueRequests(prev => prev.filter(r => r.userId !== userId));
      showMsg('success', 'League request denied.');
    } catch (err) { showMsg('error', err.message); }
  };

  const handleApproveJoinRequest = async (email) => {
    setApprovingEmail(email);
    try {
      await api.adminApproveJoinRequest({ email, leagueId: approveLeague[email] || undefined });
      setJoinRequests(prev => prev.filter(r => r.email !== email));
      setUsers(prev => [...prev, { email, name: joinRequests.find(r => r.email === email)?.name || email, createdAt: new Date().toISOString() }]);
      showMsg('success', `Invite sent to ${email}`);
    } catch (err) { showMsg('error', err.message); }
    finally { setApprovingEmail(''); }
  };

  const handleDenyJoinRequest = async (email) => {
    try {
      await api.adminDenyJoinRequest({ email });
      setJoinRequests(prev => prev.filter(r => r.email !== email));
      showMsg('success', `Request from ${email} denied.`);
    } catch (err) { showMsg('error', err.message); }
  };

  const handleSetFriendly = async (e) => {
    e.preventDefault();
    if (!friendlyForm.id || !friendlyForm.home || !friendlyForm.away || !friendlyForm.ko) return;
    setFriendlySaving(true);
    try {
      const koIso = new Date(friendlyForm.ko).toISOString();
      await api.adminSetFriendly({ ...friendlyForm, id: friendlyForm.id.toUpperCase(), ko: koIso });
      setFriendlies(prev => {
        const filtered = prev.filter(f => f.id !== friendlyForm.id.toUpperCase());
        return [...filtered, { ...friendlyForm, id: friendlyForm.id.toUpperCase(), ko: koIso }]
          .sort((a, b) => a.id.localeCompare(b.id));
      });
      showMsg('success', `Test match ${friendlyForm.id.toUpperCase()} saved.`);
    } catch (err) { showMsg('error', err.message); }
    finally { setFriendlySaving(false); }
  };

  const handleLoadCoverage = async () => {
    setCoverageLoading(true);
    setCoverageData(null);
    try {
      const data = await api.adminGetMatchCoverage(coverageMatchId);
      setCoverageData(data);
    } catch (err) { showMsg('error', `Failed to load coverage: ${err.message}`); }
    finally { setCoverageLoading(false); }
  };

  const selectedMatch = ALL_MATCHES.find(m => m.id === matchId) || friendlies.find(f => f.id === matchId);

  // True when the current user can admin the selected league specifically
  const isAdminOfSelectedLeague = isGlobalAdmin ||
    (selectedLeagueId != null && leagues.find(l => l.leagueId === selectedLeagueId)?.isLeagueAdmin === true);

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div className="page">
      <h1 className="page-title">Admin</h1>
      <p className="page-subtitle">Manage results and tournament state</p>

      {/* League admin section */}
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)', letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--text-faint)', marginBottom: '0.75rem', marginTop: '0.5rem' }}>
        League Admin
      </div>

      {msg.text && (
        <div className={msg.type === 'error' ? 'error-msg' : 'success-msg'} style={{ marginBottom: '1.5rem' }}>
          {msg.text}
        </div>
      )}

      {/* Global admin section divider */}
      {isGlobalAdmin && (
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)', letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--text-faint)', marginBottom: '0.75rem', marginTop: '0.5rem', paddingTop: '1rem', borderTop: '1px solid var(--line)' }}>
          Global Admin
        </div>
      )}

      {/* Winner celebration preview — global admin only */}
      {isGlobalAdmin && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="section-kicker" style={{ marginBottom: '0.75rem' }}>Winner celebration (preview)</div>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: '0 0 0.75rem' }}>
            Test the end-of-tournament 8-bit animation. It otherwise plays automatically once the Final result is in (team, in the Knockout view) and once the tournament ends (league winner, on the Leaderboard).
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button className="btn btn-sm" onClick={() => setCelebratePreview({ kind: 'tournament', name: 'Brazil', team: 'Brazil' })}>Preview tournament winner</button>
            <button className="btn btn-sm" onClick={() => setCelebratePreview({ kind: 'league', name: currentUser?.name || 'Test Player' })}>Preview league winner</button>
          </div>
        </div>
      )}

      {celebratePreview && (
        <WinnerCelebration
          kind={celebratePreview.kind}
          name={celebratePreview.name}
          team={celebratePreview.team}
          onClose={() => setCelebratePreview(null)}
        />
      )}

      {/* Join Requests — global admin only */}
      {isGlobalAdmin && joinRequestsLoaded && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="section-kicker" style={{ marginBottom: joinRequests.length ? '1rem' : '0' }}>
            Join Requests {joinRequests.length > 0 && <span style={{ color: 'var(--spark-bright)', marginLeft: '0.4rem' }}>({joinRequests.length})</span>}
          </div>
          {joinRequests.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: 0 }}>No pending requests.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {joinRequests.map(req => (
                <div key={req.email} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.75rem', alignItems: 'start', padding: '0.75rem', background: 'var(--bg)', borderRadius: 'var(--r-sm)', border: '1px solid var(--line)' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 'var(--t-sm)' }}>{req.name}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem', fontFamily: 'var(--font-mono)' }}>{req.email}</div>
                    <div style={{ display: 'flex', gap: '1rem', marginTop: '0.3rem', flexWrap: 'wrap' }}>
                      {req.requestedLeagueName && (
                        <span style={{ color: 'var(--volt)', fontSize: 'var(--t-xs)', fontFamily: 'var(--font-mono)' }}>
                          Requested: {req.requestedLeagueName}
                        </span>
                      )}
                      <span style={{ color: 'var(--text-faint)', fontSize: 'var(--t-xs)' }}>
                        {req.requestedAt ? format(new Date(req.requestedAt), 'dd MMM HH:mm') : ''}
                      </span>
                    </div>
                    {leagues.length > 0 && (
                      <select
                        value={approveLeague[req.email] || req.requestedLeagueId || ''}
                        onChange={e => setApproveLeague(prev => ({ ...prev, [req.email]: e.target.value }))}
                        style={{ marginTop: '0.5rem', fontSize: '0.82rem', background: 'var(--card)', color: 'var(--white)', border: '1px solid var(--border)', borderRadius: '4px', padding: '0.2rem 0.4rem', maxWidth: '220px' }}
                      >
                        <option value="">No league</option>
                        {leagues.map(l => <option key={l.leagueId} value={l.leagueId}>{l.name}</option>)}
                      </select>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
                    <button
                      className="btn btn-sm btn-primary"
                      disabled={approvingEmail === req.email}
                      onClick={() => handleApproveJoinRequest(req.email)}
                    >
                      {approvingEmail === req.email ? '…' : 'Approve'}
                    </button>
                    <button
                      className="btn btn-sm"
                      style={{ color: 'var(--danger)', borderColor: 'var(--danger-line)', background: 'var(--danger-dim)' }}
                      onClick={() => handleDenyJoinRequest(req.email)}
                    >
                      Deny
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* League Creation Requests — global admin only */}

      {/* Lock / unlock */}
      <div className="card" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <div className="section-kicker" style={{ marginBottom: '0.3rem' }}>
            Prediction Input
          </div>
          <p style={{ color: 'var(--mid)', fontSize: '0.85rem', margin: 0 }}>
            {locked
              ? 'Currently locked — all players are in read-only mode.'
              : 'Currently open — players can submit and update predictions.'}
          </p>
        </div>
        <button
          className={`btn ${locked ? 'btn-primary' : 'btn-danger'}`}
          style={{ minWidth: '9rem' }}
          onClick={handleToggleLock}
          disabled={syncing}
        >
          {locked ? 'Unlock Predictions' : 'Lock Predictions'}
        </button>
      </div>

      {/* Reminder window — global admin only */}
      {isGlobalAdmin && (
        <div className="card" style={{ marginBottom: '1.5rem', maxWidth: '480px' }}>
          <div className="section-kicker" style={{ marginBottom: '1rem' }}>
            Email Reminder Window
          </div>
          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--t-sm)', marginBottom: '1rem' }}>
            Reminders are sent when the first kick-off of a matchday is between <strong>{reminderWindow.minsBefore}</strong> and <strong>{reminderWindow.minsBefore + reminderWindow.windowMins}</strong> minutes away. The scheduler runs every 5 minutes.
          </p>
          <form onSubmit={handleSetReminderWindow} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <div className="field" style={{ flex: 1, minWidth: '140px' }}>
                <label>Minutes before KO</label>
                <input type="number" name="minsBefore" min={5} max={240} defaultValue={reminderWindow.minsBefore} required />
              </div>
              <div className="field" style={{ flex: 1, minWidth: '140px' }}>
                <label>Window (mins)</label>
                <input type="number" name="windowMins" min={1} max={60} defaultValue={reminderWindow.windowMins} required />
              </div>
            </div>
            <button type="submit" className="btn btn-primary" disabled={reminderWindowSaving} style={{ alignSelf: 'flex-start' }}>
              {reminderWindowSaving ? 'Saving…' : 'Save Window'}
            </button>
          </form>
        </div>
      )}

      {/* Leaderboard maintenance — global admin only */}
      {isGlobalAdmin && (
        <div className="card" style={{ marginBottom: '1.5rem', maxWidth: '480px' }}>
          <div className="section-kicker" style={{ marginBottom: '1rem' }}>
            Leaderboard
          </div>
          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--t-sm)', marginBottom: '1rem' }}>
            Snapshots normally rebuild automatically after every result write. Use this if snapshots are missing (e.g. after a table restore) or if you want to force a recompute without touching a result.
          </p>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={handleRebuildLeaderboard}
            disabled={rebuildState === 'running'}
          >
            {rebuildState === 'running' ? 'Rebuilding…' : 'Rebuild snapshots'}
          </button>
          {rebuildState === 'error' && (
            <p style={{ color: 'var(--danger)', fontSize: 'var(--t-xs)', fontFamily: 'var(--font-mono)', marginTop: '0.5rem' }}>
              {rebuildError}
            </p>
          )}
        </div>
      )}

      {/* Spreadsheet export / upload */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="section-kicker" style={{ marginBottom: '1rem' }}>
          Spreadsheet
        </div>

        <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>Format:</span>
            {['csv', 'xlsx'].map(f => (
              <button key={f} className={`group-tab${exportFmt === f ? ' active' : ''}`}
                style={{ padding: '0.3rem 0.75rem', fontSize: '0.82rem' }} onClick={() => setExportFmt(f)}>
                {f.toUpperCase()}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <span style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>Upload type:</span>
            {[['results', 'Results'], ['predictions', 'Predictions']].map(([val, label]) => (
              <button key={val} className={`group-tab${uploadType === val ? ' active' : ''}`}
                style={{ padding: '0.3rem 0.75rem', fontSize: '0.82rem' }} onClick={() => setUploadType(val)}>
                {label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.5rem' }}>
          {/* Export */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)' }}>Export</div>
            <button className="btn btn-ghost" onClick={() => handleExport('results')} disabled={syncing}>
              Export Results ({results.length} entered)
            </button>
            <button className="btn btn-ghost" onClick={() => handleExport('predictions')} disabled={syncing}>
              Export All Predictions
            </button>
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--t-xs)', fontFamily: 'var(--font-mono)', margin: 0 }}>
              Downloads in Prediction Entry format — compatible with the admin spreadsheet.
            </p>
          </div>

          {/* Upload */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)' }}>Upload</div>
            <button className="btn btn-ghost" style={{ fontSize: '0.82rem' }}
              onClick={() => uploadType === 'results'
                ? downloadResultsTemplate(users, exportFmt)
                : downloadPredictionsTemplate(users, exportFmt)}>
              Download {uploadType} template
            </button>
            <label className="btn btn-ghost" style={{ cursor: 'pointer', textAlign: 'center' }}>
              {syncing ? 'Uploading…' : 'Choose file (.csv / .xlsx)'}
              <input ref={uploadRef} type="file" accept=".csv,.xlsx" style={{ display: 'none' }}
                onChange={handleUpload} disabled={syncing} />
            </label>
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--t-xs)', fontFamily: 'var(--font-mono)', margin: 0 }}>
              {uploadType === 'results'
                ? 'Reads the Result column from the Prediction Entry sheet (format: "H-A", e.g. "2-1").'
                : 'Reads player prediction columns. Player names must match registered users.'}
            </p>
          </div>
        </div>
      </div>

      {/* Match coverage — league admin + */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
          <div className="section-kicker">
            Match Coverage
          </div>
          {coverageData && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setShowScores(s => !s)}
              style={{ fontSize: 'var(--t-xs)', fontFamily: 'var(--font-mono)' }}
            >
              {showScores ? 'Hide scores' : 'Show scores'}
            </button>
          )}
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '1rem' }}>
          <div className="field" style={{ flex: 1, minWidth: '200px', margin: 0 }}>
            <label style={{ fontSize: '0.78rem' }}>Match</label>
            <select value={coverageMatchId} onChange={e => { setCoverageMatchId(e.target.value); setCoverageData(null); }}>
              {[...ALL_MATCHES].sort((a, b) => parseInt(a.id.replace('M', '')) - parseInt(b.id.replace('M', ''))).map(m => (
                <option key={m.id} value={m.id}>
                  {m.id} — {koLabel(m)}
                </option>
              ))}
              {friendlies.length > 0 && <option disabled>── Test matches ──</option>}
              {friendlies.map(f => (
                <option key={f.id} value={f.id}>{f.id} — {f.home} vs {f.away} (Test)</option>
              ))}
            </select>
          </div>
          <button className="btn btn-ghost" onClick={handleLoadCoverage} disabled={coverageLoading}>
            {coverageLoading ? 'Loading…' : 'View'}
          </button>
        </div>

        {coverageData && (() => {
          const { players } = coverageData;
          const done  = players.filter(p => p.hasPrediction).length;
          const total = players.length;
          const match = ALL_MATCHES.find(m => m.id === coverageData.matchId)
            || friendlies.find(f => f.id === coverageData.matchId);
          return (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)', color: 'var(--text-muted)' }}>
                  {match?.home !== 'TBD' ? `${match?.home} vs ${match?.away}` : match?.label || coverageData.matchId}
                </span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)', color: done === total ? '#6bcb77' : 'var(--spark-bright)' }}>
                  {done}/{total} predicted
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.4rem' }}>
                {players.map(p => (
                  <div key={p.userId} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.6rem', borderRadius: 'var(--r-sm)', background: p.hasPrediction ? 'rgba(107,203,119,0.08)' : 'rgba(224,90,78,0.08)', border: `1px solid ${p.hasPrediction ? 'rgba(107,203,119,0.2)' : 'rgba(224,90,78,0.2)'}` }}>
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', flexShrink: 0, background: p.hasPrediction ? '#6bcb77' : '#e05a4e' }} />
                    <span style={{ fontSize: 'var(--t-sm)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                    {p.hasPrediction && showScores && (
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)', color: 'var(--text-muted)', flexShrink: 0 }}>
                        {p.homeScore}–{p.awayScore}{p.joker ? ' ×2' : ''}
                      </span>
                    )}
                    {p.hasPrediction && !showScores && (
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-2xs)', color: 'var(--text-faint)', flexShrink: 0 }}>✓</span>
                    )}
                  </div>
                ))}
              </div>
            </>
          );
        })()}
      </div>

      {/* Leagues — league-admin and global admin */}
      {isLeagueAdmin && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="section-kicker" style={{ marginBottom: '1rem' }}>
            Leagues ({leagues.length})
          </div>

          {/* League list */}
          {leagues.length > 0 && (
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
              {leagues.map(l => (
                <button
                  key={l.leagueId}
                  className={`chip${selectedLeagueId === l.leagueId ? ' active' : ''}`}
                  onClick={() => selectedLeagueId === l.leagueId ? setSelectedLeagueId(null) : handleSelectLeague(l.leagueId)}
                >
                  {l.name}
                </button>
              ))}
            </div>
          )}

          {/* League detail panel */}
          {selectedLeagueId && (
            <div style={{ background: 'var(--bg)', borderRadius: 'var(--r-sm)', border: '1px solid var(--line)', padding: '1rem', marginBottom: '1.25rem' }}>
              {leagueDetailLoading ? (
                <div style={{ color: 'var(--text-muted)', fontSize: 'var(--t-sm)' }}>Loading…</div>
              ) : leagueDetail ? (
                <>
                  <div style={{ fontWeight: 600, marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                    {leagueDetail.name}
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)', color: 'var(--text-muted)' }}>{leagueDetail.leagueId}</span>
                    {leagueDetail.joinCode && (
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)', background: 'var(--volt-dim)', color: 'var(--volt)', border: '1px solid var(--volt-line)', borderRadius: '4px', padding: '0.1rem 0.5rem', letterSpacing: '.1em' }}
                        title="Share this code with players so they can request access to this league">
                        Code: {leagueDetail.joinCode}
                      </span>
                    )}
                  </div>

                  {/* Members */}
                  <div style={{ fontSize: '0.78rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                    Members ({(leagueDetail.members || []).length})
                  </div>
                  {(leagueDetail.members || []).length === 0 ? (
                    <p style={{ color: 'var(--text-muted)', fontSize: 'var(--t-sm)', margin: '0 0 0.75rem' }}>No members yet.</p>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.75rem' }}>
                      {leagueDetail.members.map(m => {
                        const u = users.find(u => u.userId === m.userId);
                        return (
                          <span key={m.userId} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', background: 'var(--card)', border: '1px solid var(--line)', borderRadius: '20px', padding: '0.2rem 0.5rem 0.2rem 0.7rem', fontSize: 'var(--t-sm)' }}>
                            {u?.name || m.email || m.userId}
                            {isAdminOfSelectedLeague && (
                              <button
                                onClick={() => handleLeagueRemove(m.userId, u?.name || m.email || m.userId)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '0.9rem', padding: '0', lineHeight: 1 }}
                                title="Remove from league"
                              >×</button>
                            )}
                          </span>
                        );
                      })}
                    </div>
                  )}

                  {/* Add existing player — league admins only */}
                  {isAdminOfSelectedLeague && (() => {
                    const memberIds = new Set((leagueDetail.members || []).map(m => m.userId));
                    const nonMembers = users.filter(u => !memberIds.has(u.userId));
                    return nonMembers.length > 0 ? (
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                        <select value={leagueAddUserId} onChange={e => setLeagueAddUserId(e.target.value)} style={{ flex: 1, minWidth: '180px', fontSize: '0.85rem' }}>
                          <option value="">Add registered player…</option>
                          {nonMembers.map(u => <option key={u.userId} value={u.userId}>{u.name}</option>)}
                        </select>
                        <button className="btn btn-sm btn-ghost" disabled={!leagueAddUserId || leagueSaving === 'add'} onClick={handleLeagueAddExisting}>
                          {leagueSaving === 'add' ? '…' : 'Add'}
                        </button>
                        <button className="btn btn-sm btn-ghost" disabled={leagueSaving === 'addall'} onClick={handleLeagueAddAll}>
                          {leagueSaving === 'addall' ? 'Adding…' : `Add all ${nonMembers.length}`}
                        </button>
                      </div>
                    ) : null;
                  })()}

                  {/* Rename + Delete — league admin of this league */}
                  {isAdminOfSelectedLeague && (
                  <form onSubmit={handleRenameLeague} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                    <div className="field" style={{ flex: 1, minWidth: '180px', margin: 0 }}>
                      <label style={{ fontSize: '0.78rem' }}>Rename</label>
                      <input
                        type="text"
                        value={renameLeagueName}
                        onChange={e => setRenameLeagueName(e.target.value)}
                        placeholder={leagueDetail.name}
                        maxLength={60}
                      />
                    </div>
                    <button type="submit" className="btn btn-sm btn-ghost" disabled={leagueSaving === 'rename' || !renameLeagueName.trim()}>
                      {leagueSaving === 'rename' ? '…' : 'Rename'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm"
                      style={{ color: 'var(--danger)', borderColor: 'var(--danger-line)', background: 'var(--danger-dim)' }}
                      onClick={handleDeleteLeague}
                      disabled={leagueSaving === 'delete'}
                    >
                      {leagueSaving === 'delete' ? 'Deleting…' : 'Delete League'}
                    </button>
                  </form>
                  )}

                  {/* Invite new player to league — league admins only */}
                  {isAdminOfSelectedLeague && (
                    <>
                      <div style={{ fontSize: '0.78rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                        Invite new player
                      </div>
                      <form onSubmit={handleLeagueInvite} style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <input
                          type="email"
                          value={leagueInviteEmail}
                          onChange={e => setLeagueInviteEmail(e.target.value)}
                          placeholder="player@example.com"
                          style={{ flex: 1, minWidth: '200px', fontSize: '0.85rem' }}
                          required
                        />
                        <button type="submit" className="btn btn-primary btn-sm" disabled={leagueSaving === 'invite' || !leagueInviteEmail}>
                          {leagueSaving === 'invite' ? 'Sending…' : 'Invite to league'}
                        </button>
                      </form>
                    </>
                  )}
                </>
              ) : null}
            </div>
          )}

          {/* Create league form — global admin only */}
          {isGlobalAdmin && (
          <form onSubmit={handleCreateLeague} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="field" style={{ flex: 1, minWidth: '200px', margin: 0 }}>
              <label style={{ fontSize: '0.78rem' }}>New league name</label>
              <input
                type="text"
                value={leagueName}
                onChange={e => setLeagueName(e.target.value)}
                placeholder="e.g. Office League"
                maxLength={60}
                required
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={leagueCreating || !leagueName.trim()}>
              {leagueCreating ? 'Creating…' : 'Create League'}
            </button>
          </form>
          )}
        </div>
      )}

      {/* Invite player — global admin only */}
      {isGlobalAdmin && <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="section-kicker" style={{ marginBottom: '1rem' }}>
          Invite Player
        </div>
        <form onSubmit={handleInvite} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '400px' }}>
          <div className="field">
            <label>Email address</label>
            <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
              placeholder="player@example.com" required />
          </div>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.83rem', margin: 0 }}>
            They'll receive an email with a one-time password and set their own display name on first sign-in. Expires in 24 hours.
          </p>
          <button type="submit" className="btn btn-primary" disabled={syncing || !inviteEmail}>
            {syncing ? 'Sending…' : 'Send Invite'}
          </button>
        </form>
      </div>}

      {/* Players */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="section-kicker" style={{ marginBottom: '1rem' }}>
          Players ({users.length})
        </div>
        {users.length === 0 ? (
          <p style={{ color: 'var(--mid)' }}>No players yet.</p>
        ) : (
          <div className="admin-table-wrap">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Name', 'Email', 'Joined', 'Role', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '0.5rem', color: 'var(--mid)', fontSize: '0.78rem', textTransform: 'uppercase', borderBottom: '1px solid var(--border)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const isSelf = u.userId === currentUser?.sub;
                const userRole = u.role || 'user';
                const isPlaceholder = !!u.placeholder;
                return (
                  <tr key={u.userId}>
                    <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--border)' }}>
                      {renameUserId === u.userId ? (
                        <span style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                          <input
                            type="text"
                            value={renameNewName}
                            onChange={e => setRenameNewName(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleRenamePlayer(u.userId); if (e.key === 'Escape') setRenameUserId(null); }}
                            maxLength={50}
                            style={{ fontSize: '0.82rem', padding: '2px 6px', background: 'var(--bg)', color: 'var(--white)', border: '1px solid var(--border)', borderRadius: '4px', width: '150px' }}
                            autoFocus
                          />
                          <button className="btn btn-sm btn-primary" disabled={renameSaving || !renameNewName.trim()} onClick={() => handleRenamePlayer(u.userId)}>Save</button>
                          <button className="btn btn-sm btn-ghost" onClick={() => setRenameUserId(null)}>✕</button>
                        </span>
                      ) : (
                        <span style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                          {u.name}
                          {isPlaceholder && <span style={{ fontSize: '0.7rem', background: 'var(--spark-dim)', color: 'var(--spark-bright)', border: '1px solid var(--spark-line)', borderRadius: '4px', padding: '0 0.35rem', fontFamily: 'var(--font-mono)' }}>PH</span>}
                          {isGlobalAdmin && !isSelf && (
                            <button
                              className="btn btn-sm btn-ghost"
                              style={{ padding: '1px 5px', fontSize: '0.7rem' }}
                              onClick={() => { setRenameUserId(u.userId); setRenameNewName(u.name); }}
                            >
                              Rename
                            </button>
                          )}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--border)', color: 'var(--mid)', fontSize: '0.88rem' }}>
                      {isPlaceholder
                        ? (u.invitedEmail ? <span style={{ color: 'var(--text-faint)' }}>Invite pending: {u.invitedEmail}</span> : <span style={{ color: 'var(--text-faint)' }}>No email yet</span>)
                        : editEmailUserId === u.userId
                          ? <span style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                              <input
                                type="email"
                                value={editEmailVal}
                                onChange={e => setEditEmailVal(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') handleSetEmail(u.userId); if (e.key === 'Escape') setEditEmailUserId(null); }}
                                style={{ fontSize: '0.82rem', padding: '2px 6px', background: 'var(--bg)', color: 'var(--white)', border: '1px solid var(--border)', borderRadius: '4px', width: '180px' }}
                                autoFocus
                              />
                              <button className="btn btn-sm btn-primary" disabled={editEmailSaving || !editEmailVal.trim()} onClick={() => handleSetEmail(u.userId)}>Save</button>
                              <button className="btn btn-sm btn-ghost" onClick={() => setEditEmailUserId(null)}>✕</button>
                            </span>
                          : <span style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                              <span>{u.email || <span style={{ color: 'var(--text-faint)' }}>—</span>}</span>
                              {isGlobalAdmin && !isSelf && (
                                <button
                                  className="btn btn-sm btn-ghost"
                                  style={{ padding: '1px 5px', fontSize: '0.7rem' }}
                                  onClick={() => { setEditEmailUserId(u.userId); setEditEmailVal(u.email || ''); }}
                                >
                                  Edit
                                </button>
                              )}
                            </span>
                      }
                    </td>
                    <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--border)', color: 'var(--mid)', fontSize: '0.85rem' }}>
                      {u.createdAt ? format(new Date(u.createdAt), 'dd MMM yyyy') : '—'}
                    </td>
                    <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--border)' }}>
                      {isLeagueAdmin && !isSelf ? (
                        leagueAssignUserId === u.userId ? (
                          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '0.82rem', color: 'var(--volt)', whiteSpace: 'nowrap' }}>League Admin — add to league:</span>
                            <select
                              value={leagueAssignLeagueId}
                              onChange={e => setLeagueAssignLeagueId(e.target.value)}
                              style={{ fontSize: '0.8rem', padding: '0.2rem 0.4rem', background: 'var(--card)', color: 'var(--white)', border: '1px solid var(--border)', borderRadius: '4px' }}
                            >
                              <option value="">Skip</option>
                              {leagues.map(l => <option key={l.leagueId} value={l.leagueId}>{l.name}</option>)}
                            </select>
                            <button className="btn btn-sm btn-primary" disabled={leagueAssigning} onClick={() => handleLeagueAssign(u.userId)}>
                              {leagueAssigning ? '…' : leagueAssignLeagueId ? 'Assign' : 'Skip'}
                            </button>
                          </div>
                        ) : (
                          <select
                            value={userRole}
                            onChange={e => handleSetRole(u.userId, e.target.value)}
                            style={{ fontSize: '0.82rem', padding: '0.2rem 0.4rem', background: 'var(--card)', color: 'var(--white)', border: '1px solid var(--border)', borderRadius: '4px' }}
                          >
                            <option value="user">Player</option>
                            <option value="league-admin">League Admin</option>
                            {isGlobalAdmin && <option value="admin">Global Admin</option>}
                          </select>
                        )
                      ) : (
                        <span style={{ fontSize: '0.82rem', color: isSelf ? 'var(--gold)' : 'var(--mid)' }}>
                          {isSelf
                            ? 'Global Admin (you)'
                            : userRole === 'admin' ? 'Global Admin'
                            : userRole === 'league-admin' ? 'League Admin'
                            : 'Player'}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                        {isPlaceholder ? (
                          <>
                            {!u.invitedEmail && (
                              <button className="btn btn-sm btn-ghost" onClick={() => { setPhInviteUserId(u.userId); setPhInviteEmail(''); }}>
                                Invite
                              </button>
                            )}
                            <button
                              className="btn btn-sm"
                              style={{ color: 'var(--danger)', borderColor: 'var(--danger-line)', background: 'var(--danger-dim)' }}
                              onClick={() => handleDeletePlaceholder(u.userId, u.name)}
                            >
                              Delete
                            </button>
                          </>
                        ) : (
                          <>
                            {isLeagueAdmin && !isSelf && (
                              <button className="btn btn-sm btn-ghost" onClick={() => handleResetPassword(u.userId, u.name)} title="Send password reset email">
                                Reset pwd
                              </button>
                            )}
                            {isLeagueAdmin && !isSelf && (
                              <button
                                className="btn btn-sm"
                                style={{ color: 'var(--danger)', borderColor: 'var(--danger-line)', background: 'var(--danger-dim)' }}
                                onClick={() => handleDeleteUser(u.userId, u.name)}
                                title={isGlobalAdmin ? 'Permanently delete this player' : 'Delete player (only if exclusively in your league)'}
                              >
                                Delete
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Placeholder Users — create + invite */}
      {isLeagueAdmin && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="section-kicker" style={{ marginBottom: '1rem' }}>
            Placeholder Players
          </div>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.83rem', marginBottom: '0.75rem', marginTop: 0 }}>
            Create a placeholder for a player who isn't registered yet. Enter their predictions manually, then invite them with their email when ready — their data transfers automatically on first login.
          </p>

          {/* Create placeholder form */}
          <form onSubmit={handleCreatePlaceholder} style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '1rem' }}>
            <div className="field" style={{ flex: '1', minWidth: '140px' }}>
              <label>Display name</label>
              <input value={phName} onChange={e => setPhName(e.target.value)} placeholder="John Doe" maxLength={50} required />
            </div>
            {leagues.length > 0 && (
              <div className="field" style={{ flex: '1', minWidth: '140px' }}>
                <label>Add to league</label>
                <select value={phLeagueId} onChange={e => setPhLeagueId(e.target.value)}>
                  <option value="">None</option>
                  {leagues.map(l => <option key={l.leagueId} value={l.leagueId}>{l.name}</option>)}
                </select>
              </div>
            )}
            <button type="submit" className="btn btn-primary btn-sm" disabled={phCreating || !phName.trim()}>
              {phCreating ? 'Creating…' : 'Create placeholder'}
            </button>
          </form>

          {/* Invite placeholder form */}
          {users.filter(u => u.placeholder && !u.invitedEmail).length > 0 && (
            <>
              <div style={{ borderTop: '1px solid var(--line)', paddingTop: '0.75rem', marginTop: '0.25rem' }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                  Invite a placeholder — assigns an email and sends login credentials
                </div>
                <form onSubmit={handleInvitePlaceholder} style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div className="field" style={{ flex: '1', minWidth: '140px' }}>
                    <label>Placeholder</label>
                    <select value={phInviteUserId} onChange={e => setPhInviteUserId(e.target.value)} required>
                      <option value="">Select…</option>
                      {users.filter(u => u.placeholder && !u.invitedEmail).map(u => (
                        <option key={u.userId} value={u.userId}>{u.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="field" style={{ flex: '1', minWidth: '180px' }}>
                    <label>Email to invite</label>
                    <input type="email" value={phInviteEmail} onChange={e => setPhInviteEmail(e.target.value)} placeholder="player@example.com" required />
                  </div>
                  <button type="submit" className="btn btn-primary btn-sm" disabled={!!phInviting || !phInviteUserId || !phInviteEmail}>
                    {phInviting ? 'Sending…' : 'Send invite'}
                  </button>
                </form>
              </div>
            </>
          )}
        </div>
      )}

      {/* Proxy Predictions — enter predictions on behalf of any player */}
      {isLeagueAdmin && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="section-kicker" style={{ marginBottom: '1rem' }}>
            Proxy Predictions
          </div>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.83rem', marginBottom: '0.75rem', marginTop: 0 }}>
            Enter predictions for any player (including placeholders). Kick-off lock is bypassed for manual entry.
          </p>
          <form onSubmit={handleSaveProxyPrediction} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div className="field" style={{ flex: '1', minWidth: '160px' }}>
                <label>Player</label>
                <select value={proxyUserId} onChange={e => { setProxyUserId(e.target.value); setProxyWinner(''); loadProxyPreds(e.target.value); }} required>
                  <option value="">Select player…</option>
                  {users.map(u => (
                    <option key={u.userId} value={u.userId}>{u.name}{u.placeholder ? ' [PH]' : ''}</option>
                  ))}
                </select>
              </div>
              <div className="field" style={{ flex: '1', minWidth: '180px' }}>
                <label>Match</label>
                <select value={proxyMatchId} onChange={e => {
                  const val = e.target.value;
                  setProxyMatchId(val);
                  if (val === 'DEFAULT') {
                    setProxyHome(proxyExistingDefault ? String(proxyExistingDefault.homeScore) : '');
                    setProxyAway(proxyExistingDefault ? String(proxyExistingDefault.awayScore) : '');
                    setProxyJoker(false);
                  } else {
                    const existing = proxyPreds.find(p => p.matchId === val);
                    setProxyHome(existing ? String(existing.homeScore) : '');
                    setProxyAway(existing ? String(existing.awayScore) : '');
                    setProxyJoker(existing?.joker || false);
                  }
                }}>
                  <option value="DEFAULT">DEFAULT — fallback score for all unset matches{proxyExistingDefault ? ` (${proxyExistingDefault.homeScore}–${proxyExistingDefault.awayScore})` : ''}</option>
                  {(() => {
                    const now = Date.now();
                    const label = (m) => `${m.id} — ${koLabel(m)} · ${format(new Date(m.ko), 'd MMM HH:mm')}`;
                    const upcoming = ALL_MATCHES.filter(m => matchKoMs(m) > now).sort((a, b) => matchKoMs(a) - matchKoMs(b));
                    const played   = ALL_MATCHES.filter(m => matchKoMs(m) <= now).sort((a, b) => matchKoMs(b) - matchKoMs(a));
                    return (
                      <>
                        {upcoming.length > 0 && (
                          <optgroup label="Upcoming (soonest first)">
                            {upcoming.map(m => <option key={m.id} value={m.id}>{label(m)}</option>)}
                          </optgroup>
                        )}
                        {played.length > 0 && (
                          <optgroup label="Played (most recent first)">
                            {played.map(m => <option key={m.id} value={m.id}>{label(m)}</option>)}
                          </optgroup>
                        )}
                      </>
                    );
                  })()}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <div className="score-input">
                <input type="number" min="0" max="30" value={proxyHome} onChange={e => setProxyHome(e.target.value)} placeholder="H" required />
                <span className="score-separator">–</span>
                <input type="number" min="0" max="30" value={proxyAway} onChange={e => setProxyAway(e.target.value)} placeholder="A" required />
              </div>
              {proxyMatchId !== 'DEFAULT' && (
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem', color: 'var(--text-muted)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={proxyJoker} onChange={e => setProxyJoker(e.target.checked)} />
                  Joker ×2
                </label>
              )}
              {proxyPredsLoading && <span style={{ fontSize: '0.8rem', color: 'var(--text-faint)' }}>Loading…</span>}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <button type="submit" className="btn btn-primary btn-sm" disabled={proxySaving || !proxyUserId || proxyHome === '' || proxyAway === ''}>
                {proxySaving ? 'Saving…' : 'Save prediction'}
              </button>
              {proxyPreds.length > 0 && (
                <span style={{ fontSize: 'var(--t-xs)', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                  {proxyPreds.length} prediction{proxyPreds.length !== 1 ? 's' : ''} set
                </span>
              )}
            </div>
          </form>

          {/* Winner prediction for proxy player */}
          {proxyUserId && (
            <div style={{ marginTop: '1rem', borderTop: '1px solid var(--line)', paddingTop: '0.75rem' }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)', color: 'var(--text-muted)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '.08em' }}>
                Tournament winner pick
                {proxyExistingWinner && <span style={{ color: 'var(--volt)', marginLeft: '0.5rem' }}>— currently: {proxyExistingWinner}</span>}
              </div>
              <form onSubmit={handleSaveProxyWinner} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <select value={proxyWinner} onChange={e => setProxyWinner(e.target.value)} style={{ flex: 1, minWidth: '160px', fontSize: '0.85rem' }}>
                  <option value="">Select team…</option>
                  {TEAMS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <button type="submit" className="btn btn-primary btn-sm" disabled={proxyWinnerSaving || !proxyWinner}>
                  {proxyWinnerSaving ? 'Saving…' : 'Save winner'}
                </button>
              </form>
            </div>
          )}

          {/* Compact list of this player's predictions */}
          {proxyUserId && proxyPreds.length > 0 && (
            <div style={{ marginTop: '1rem', borderTop: '1px solid var(--line)', paddingTop: '0.75rem' }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)', color: 'var(--text-muted)', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '.08em' }}>Existing predictions</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(130px,1fr))', gap: '0.3rem' }}>
                {proxyPreds.sort((a, b) => {
                  const ai = parseInt(a.matchId?.replace('M', '') || '0');
                  const bi = parseInt(b.matchId?.replace('M', '') || '0');
                  return ai - bi;
                }).map(p => (
                  <button key={p.matchId} className="btn btn-ghost btn-sm"
                    style={{ justifyContent: 'space-between', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)' }}
                    onClick={() => {
                      setProxyMatchId(p.matchId);
                      setProxyHome(String(p.homeScore));
                      setProxyAway(String(p.awayScore));
                      setProxyJoker(p.joker || false);
                    }}>
                    <span style={{ color: 'var(--text-faint)' }}>{p.matchId}</span>
                    <span>{p.homeScore}–{p.awayScore}{p.joker ? ' ×' : ''}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Knockout results — review & override (global admin only) */}
      {isGlobalAdmin && (() => {
        const now = Date.now();
        const rows = KNOCKOUT_MATCHES.map(m => {
          const ov = knockoutTeams[m.id];
          const home = ov?.home || m.home;
          const away = ov?.away || m.away;
          const res = results.find(r => r.matchId === m.id);
          const hasRes = res && res.homeScore !== undefined && res.homeScore !== null;
          const kickedOff = new Date(m.ko).getTime() < now;
          let status = 'upcoming';
          if (hasRes) status = res.needsReview ? 'review' : 'done';
          else if (kickedOff) status = 'missing';
          return { m, home, away, res, status, ko: new Date(m.ko) };
        }).sort((a, b) => a.ko - b.ko);
        const shown = rows.filter(r => r.status !== 'upcoming');
        const upcoming = rows.length - shown.length;
        const needCount = rows.filter(r => r.status === 'missing' || r.status === 'review').length;
        const badge = { done: ['✓ done', 'var(--good)'], missing: ['needs result', 'var(--danger)'], review: ['⚠ review', 'var(--spark-bright)'] };
        const editRow = (m, res) => {
          setMatchId(m.id);
          setHomeScore(res?.homeScore != null ? String(res.homeScore) : '');
          setAwayScore(res?.awayScore != null ? String(res.awayScore) : '');
          setAetHome(res?.aetHome != null ? String(res.aetHome) : '');
          setAetAway(res?.aetAway != null ? String(res.aetAway) : '');
          setPenHome(res?.penHome != null ? String(res.penHome) : '');
          setPenAway(res?.penAway != null ? String(res.penAway) : '');
          setKoWinner(res?.winner || '');
          resultFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        };
        return (
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div className="section-kicker" style={{ marginBottom: '0.75rem' }}>
              Knockout results — review &amp; override
              {needCount > 0 && <span style={{ color: 'var(--spark-bright)', marginLeft: '0.4rem' }}>({needCount} need attention)</span>}
            </div>
            {shown.length === 0 ? (
              <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: 0 }}>No knockout matches have kicked off yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {shown.map(({ m, home, away, res, status }) => {
                  const [lbl, col] = badge[status] || ['', 'var(--text-muted)'];
                  const attn = status === 'missing' || status === 'review';
                  return (
                    <div key={m.id} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto auto', gap: '0.6rem', alignItems: 'center', padding: '0.4rem 0.5rem', background: 'var(--ink-750)', borderRadius: 'var(--r-sm)', border: `1px solid ${attn ? col : 'var(--line)'}` }}>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.7rem', color: 'var(--text-faint)' }}>{m.id}</span>
                      <span style={{ fontSize: '0.85rem' }}>{home} <span style={{ color: 'var(--text-faint)' }}>v</span> {away}</span>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: col, textAlign: 'right' }}>
                        {res && res.homeScore != null
                          ? <>{res.homeScore}–{res.awayScore}{res.winner ? ` · ${res.winner}` : ''}{res.needsReview ? ' ⚠' : ''}</>
                          : lbl}
                      </span>
                      <button type="button" className="btn btn-sm btn-ghost" onClick={() => editRow(m, res)}>Edit</button>
                    </div>
                  );
                })}
                {upcoming > 0 && <p style={{ color: 'var(--text-faint)', fontSize: '0.75rem', margin: '0.3rem 0 0' }}>+{upcoming} upcoming, not yet kicked off</p>}
              </div>
            )}
          </div>
        );
      })()}

      {/* Results entry + tournament winner — global admin only */}
      {isGlobalAdmin && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        {/* Enter result */}
        <div className="card" ref={resultFormRef}>
          <div className="section-kicker" style={{ marginBottom: '1rem' }}>
            Enter Match Result
          </div>
          <form onSubmit={handleSetResult} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div className="field">
              <label>Match</label>
              <select value={matchId} onChange={e => {
                setMatchId(e.target.value);
                const existing = results.find(r => r.matchId === e.target.value);
                setHomeScore(existing ? String(existing.homeScore) : '');
                setAwayScore(existing ? String(existing.awayScore) : '');
              }}>
                {[...ALL_MATCHES].sort((a, b) => parseInt(a.id.replace('M', '')) - parseInt(b.id.replace('M', ''))).map(m => (
                  <option key={m.id} value={m.id}>
                    {m.id} — {koLabel(m)}
                  </option>
                ))}
                {friendlies.length > 0 && <option disabled>── Test matches ──</option>}
                {friendlies.map(f => (
                  <option key={f.id} value={f.id}>
                    {f.id} — {f.home} vs {f.away} (Test)
                  </option>
                ))}
              </select>
            </div>

            {selectedMatch && selectedMatch.home !== 'TBD' && (
              <div style={{ color: 'var(--mid)', fontSize: '0.85rem' }}>
                {selectedMatch.home} vs {selectedMatch.away}
                {selectedMatch.ko && ` — ${format(new Date(selectedMatch.ko), 'dd MMM HH:mm')}`}
              </div>
            )}

            {results.find(r => r.matchId === matchId) && (
              <div style={{ color: '#6bcb77', fontSize: '0.85rem' }}>
                ✓ Current result: {results.find(r => r.matchId === matchId)?.homeScore}–{results.find(r => r.matchId === matchId)?.awayScore}
              </div>
            )}

            <div>
              <div style={{ fontSize: '0.78rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--mid)', marginBottom: '0.4rem' }}>
                90-minute result
              </div>
              <div className="score-input">
                <input type="number" min="0" max="20" value={homeScore}
                  onChange={e => setHomeScore(e.target.value)} placeholder="0" />
                <span className="score-separator">–</span>
                <input type="number" min="0" max="20" value={awayScore}
                  onChange={e => setAwayScore(e.target.value)} placeholder="0" />
              </div>
            </div>

            {KNOCKOUT_MATCHES.some(m => m.id === matchId) && (
              <div style={{ borderTop: '1px solid var(--line)', paddingTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--mid)' }}>
                  Knockout actual result <span style={{ textTransform: 'none', fontWeight: 400, color: 'var(--text-faint)' }}>— optional, display only (backup until auto-fetched)</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                  <div className="field"><label>After extra time</label>
                    <div className="score-input">
                      <input type="number" min="0" max="30" value={aetHome} onChange={e => setAetHome(e.target.value)} placeholder="–" />
                      <span className="score-separator">–</span>
                      <input type="number" min="0" max="30" value={aetAway} onChange={e => setAetAway(e.target.value)} placeholder="–" />
                    </div>
                  </div>
                  <div className="field"><label>Penalties</label>
                    <div className="score-input">
                      <input type="number" min="0" max="30" value={penHome} onChange={e => setPenHome(e.target.value)} placeholder="–" />
                      <span className="score-separator">–</span>
                      <input type="number" min="0" max="30" value={penAway} onChange={e => setPenAway(e.target.value)} placeholder="–" />
                    </div>
                  </div>
                </div>
                <div className="field"><label>Winner (advances)</label>
                  <select value={koWinner} onChange={e => setKoWinner(e.target.value)}>
                    <option value="">— none —</option>
                    {[knockoutTeams[matchId]?.home || selectedMatch?.home, knockoutTeams[matchId]?.away || selectedMatch?.away]
                      .filter(t => t && t !== 'TBD')
                      .map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
            )}

            <button type="submit" className="btn btn-primary" disabled={syncing || homeScore === '' || awayScore === ''}>
              {syncing ? 'Saving…' : 'Save Result'}
            </button>
          </form>
        </div>

        {/* Tournament winner */}
        <div className="card">
          <div className="section-kicker" style={{ marginBottom: '1rem' }}>
            Set Tournament Winner
          </div>
          <form onSubmit={handleSetWinner} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <p style={{ color: 'var(--mid)', fontSize: '0.85rem' }}>
              Set this after the final. This triggers winner-prediction points for all players.
            </p>
            <div className="field">
              <label>Champion</label>
              <select value={tournamentWinner} onChange={e => setTournamentWinner(e.target.value)}>
                <option value="">-- Select winner --</option>
                {TEAMS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <button type="submit" className="btn btn-primary" disabled={syncing || !tournamentWinner}>
              {syncing ? 'Saving…' : 'Set Champion'}
            </button>
          </form>
        </div>
      </div>}

      {/* Results entered — global admin only */}
      {isGlobalAdmin &&
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="section-kicker" style={{ marginBottom: '1rem' }}>
          Results Entered ({results.length}/104)
        </div>
        {results.length === 0 ? (
          <p style={{ color: 'var(--mid)' }}>No results entered yet.</p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {results
              .sort((a, b) => parseInt(a.matchId.replace('M', '')) - parseInt(b.matchId.replace('M', '')))
              .map(r => (
                <span key={r.matchId} style={{
                  background: 'rgba(107,203,119,0.1)',
                  border: '1px solid rgba(107,203,119,0.3)',
                  borderRadius: '4px',
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.82rem',
                }}>
                  {r.matchId}: {r.homeScore}–{r.awayScore}
                  {r.manualEntry && <span style={{ marginLeft: '0.3rem', color: 'var(--mid)' }}>✎</span>}
                </span>
              ))}
          </div>
        )}
      </div>}

      {/* Matchday Override — global admin only */}
      {isGlobalAdmin && <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="section-kicker" style={{ marginBottom: '1rem' }}>
          Matchday Assignment
        </div>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.83rem', marginBottom: '1rem' }}>
          Override the matchday number for a group-stage match. These take precedence over the static values sourced from the fixture data.
        </p>
        {Object.keys(matchdayOverrides).length > 0 && (
          <div style={{ marginBottom: '1rem', display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
            {Object.entries(matchdayOverrides).sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true })).map(([matchId, md]) => (
              <span key={matchId} style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)', background: 'var(--surface-2)', borderRadius: '4px', padding: '2px 6px' }}>
                {matchId} → MD{md}
              </span>
            ))}
          </div>
        )}
        <form onSubmit={handleSetMatchdayOverride} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="field" style={{ flex: '1 1 160px' }}>
            <label>Match</label>
            <select value={matchdayEditId} onChange={e => {
              setMatchdayEditId(e.target.value);
              const existing = matchdayOverrides[e.target.value];
              if (existing) setMatchdayEditValue(String(existing));
              else {
                const staticMd = GROUP_MATCHES.find(m => m.id === e.target.value)?.matchday;
                setMatchdayEditValue(staticMd ? String(staticMd) : '');
              }
            }}>
              {GROUP_MATCHES.map(m => (
                <option key={m.id} value={m.id}>
                  {m.id} · {m.home} vs {m.away} (MD{matchdayOverrides[m.id] ?? m.matchday})
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ flex: '0 0 80px' }}>
            <label>Matchday</label>
            <input type="number" min="1" max="17" value={matchdayEditValue}
              onChange={e => setMatchdayEditValue(e.target.value)} placeholder="1–17" required />
          </div>
          <button className="btn btn-primary btn-sm" disabled={matchdaySaving} style={{ marginBottom: '0.1rem' }}>
            {matchdaySaving ? '…' : 'Save'}
          </button>
        </form>
      </div>}

      {/* Knockout Teams — global admin only */}
      {isGlobalAdmin && <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="section-kicker" style={{ marginBottom: '1rem' }}>
          Knockout Teams
        </div>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.83rem', marginBottom: '1rem' }}>
          Set the teams for each knockout match. Manual entries override auto-sync. Used when the API hasn't populated them yet.
        </p>
        <div className="chip-strip" style={{ marginBottom: '1rem' }}>
          {[{key:'R32',label:'Rd of 32'},{key:'R16',label:'Rd of 16'},{key:'QF',label:'QF'},{key:'SF',label:'SF'}].map(r => (
            <button key={r.key} className={`chip ${knockoutRound === r.key ? 'active' : ''}`} onClick={() => setKnockoutRound(r.key)}>{r.label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {KNOCKOUT_MATCHES.filter(m => m.round === knockoutRound).map(m => {
            const existing = knockoutTeams[m.id];
            const form = knockoutForm[m.id] || { home: '', away: '' };
            return (
              <div key={m.id} style={{ display: 'grid', gridTemplateColumns: '3rem 1fr 1fr auto', gap: '0.5rem', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid var(--line)' }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)', color: 'var(--volt)' }}>{m.id}</span>
                <select value={form.home || ''} onChange={e => setKnockoutForm(f => ({ ...f, [m.id]: { ...form, home: e.target.value } }))}>
                  <option value="">Home team…</option>
                  {TEAMS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <select value={form.away || ''} onChange={e => setKnockoutForm(f => ({ ...f, [m.id]: { ...form, away: e.target.value } }))}>
                  <option value="">Away team…</option>
                  {TEAMS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                  {existing && <span title="Manually set" style={{ color: 'var(--volt)', fontSize: 'var(--t-xs)' }}>✎</span>}
                  <button className="btn btn-sm btn-primary" disabled={knockoutSaving === m.id || !form.home || !form.away} onClick={() => handleSetKnockoutTeams(m.id)}>
                    {knockoutSaving === m.id ? '…' : 'Set'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>}

      {/* Test matches — global admin only */}
      {isGlobalAdmin && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="section-kicker" style={{ marginBottom: '1rem' }}>
            Test Matches
          </div>
          <p style={{ color: 'var(--mid)', fontSize: '0.85rem', marginBottom: '1rem' }}>
            Test matches appear in the Friendlies tab and don't count toward points. Use them to verify predictions and results end-to-end.
          </p>

          {friendlies.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.25rem' }}>
              {friendlies.sort((a, b) => a.id.localeCompare(b.id)).map(f => (
                <button
                  key={f.id}
                  className="chip"
                  onClick={() => setFriendlyForm({
                    id: f.id,
                    home: f.home || '',
                    away: f.away || '',
                    ko: f.ko ? format(new Date(f.ko), "yyyy-MM-dd'T'HH:mm") : '',
                    label: f.label || '',
                  })}
                  title="Click to edit"
                >
                  {f.id} · {f.home} vs {f.away}
                </button>
              ))}
            </div>
          )}

          <form onSubmit={handleSetFriendly} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', alignItems: 'end' }}>
            <div className="field">
              <label>ID</label>
              <input type="text" value={friendlyForm.id} maxLength={4}
                onChange={e => setFriendlyForm(f => ({ ...f, id: e.target.value.toUpperCase() }))}
                placeholder="F1" pattern="F[1-9]" title="F1 through F9" required />
            </div>
            <div className="field">
              <label>Home</label>
              <select value={friendlyForm.home} onChange={e => setFriendlyForm(f => ({ ...f, home: e.target.value }))} required>
                <option value="">— Select —</option>
                {TEAMS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Away</label>
              <select value={friendlyForm.away} onChange={e => setFriendlyForm(f => ({ ...f, away: e.target.value }))} required>
                <option value="">— Select —</option>
                {TEAMS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Kick-off (local time)</label>
              <input type="datetime-local" value={friendlyForm.ko}
                onChange={e => setFriendlyForm(f => ({ ...f, ko: e.target.value }))} required />
            </div>
            <div className="field">
              <label>Label (optional)</label>
              <input type="text" value={friendlyForm.label} maxLength={40}
                onChange={e => setFriendlyForm(f => ({ ...f, label: e.target.value }))}
                placeholder="e.g. Pre-tournament test" />
            </div>
            <button type="submit" className="btn btn-primary" disabled={friendlySaving || !friendlyForm.id || !friendlyForm.home || !friendlyForm.away || !friendlyForm.ko}>
              {friendlySaving ? 'Saving…' : 'Save test match'}
            </button>
          </form>
        </div>
      )}

      {/* Audit log link — global admin only */}
      {isGlobalAdmin && (
        <Link to="/audit" style={{ display: 'block', textDecoration: 'none', marginBottom: '1.5rem' }}>
          <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
            <div>
              <div className="section-kicker" style={{ marginBottom: '0.2rem' }}>Audit Log</div>
              <div style={{ color: 'var(--text-muted)', fontSize: 'var(--t-sm)' }}>View all admin and player activity</div>
            </div>
            <span style={{ color: 'var(--text-faint)', fontSize: 'var(--t-lg)' }}>›</span>
          </div>
        </Link>
      )}
    </div>
  );
}
